import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';

import {
  ApolloLink,
  FetchResult,
  NextLink,
  Observable,
  ObservableSubscription,
  Observer,
  Operation
} from '@apollo/client/core';
import { GraphQLError } from 'graphql';
import { finalize } from 'rxjs';

import { PlatformService, PreloadType, PreloaderService, RedirectService, TokenService } from '@hdms/shared/util-core';

import { GQL_SESSION_EXPIRED } from '../helper';
import { OauthService } from '../oauth';
import { UserContextService } from '../user';

/**
 * AuthLink checks for errors caused by invalid / expired token
 * and either redirects to cloud login or re-authenticates silently.
 */
export class AuthLink extends ApolloLink {
  private readonly redirect = inject(RedirectService);
  private readonly oauth = inject(OauthService);
  private readonly preloader = inject(PreloaderService);
  private readonly platform = inject(PlatformService);
  private readonly tokenService = inject(TokenService);
  private readonly userContext = inject(UserContextService);

  private pendingOperations: (() => ObservableSubscription)[] = [];
  private pendingSubscriptions: ObservableSubscription[] = [];
  private isRefreshing = false;

  /**
   * Intercept graphql requests when logged in on client-side:
   *
   * - if no token present, silently fetch a new token before executing the request.
   * - if response returns 401 error, silently fetch a new token.
   * - if token retrieval fails, redirect to cloud login.
   *
   * @param operation
   * @param forward
   */
  override request(operation: Operation, forward: NextLink): Observable<FetchResult> {
    return this.platform.isBrowser && this.userContext.isLoggedIn
      ? new Observable(obs => {
          // do silent auth refresh but save pending operations
          // for re-execution after successful token refresh
          const doAuthRefresh = (error?: HttpErrorResponse): void => {
            this.pendingOperations.push(() => forward(operation).subscribe(obs));
            this.refreshAuth(obs, error);
          };

          let subs: ObservableSubscription;

          // if token not present, queue graphql request while token is being requested.
          // this situation can happen when switching from Hybris to Universal (login-sync).
          if (!this.tokenService.hasBearerToken) {
            doAuthRefresh();
          } else {
            subs = forward(operation).subscribe({
              next: obs.next.bind(obs),
              error: (error: HttpErrorResponse) => {
                // only care about unauthorized errors,
                // indicating issues with used bearer token,
                // which likely is expired.
                if (error.status === 401) {
                  doAuthRefresh(error);
                  return;
                }

                obs.error(error);
              },
              complete: obs.complete.bind(obs)
            });
          }

          return () => {
            subs?.unsubscribe();
            this.pendingSubscriptions = this.pendingSubscriptions.filter(s => {
              s.closed && s.unsubscribe();
              return !s.closed;
            });
          };
        })
      : forward(operation);
  }

  /**
   * Refresh authorization by silently fetching token from cloud login.
   * Afterwards execute the pending operations
   * @param obs
   * @param responseError
   * @private
   */
  private refreshAuth(obs: Observer<FetchResult>, responseError: HttpErrorResponse): void {
    if (this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;

    this.oauth
      .refreshAuthToken$()
      .pipe(
        finalize(() => {
          this.isRefreshing = false;
          this.pendingOperations = [];
        })
      )
      .subscribe({
        next: _ => {
          this.pendingSubscriptions = this.pendingOperations.map(subscribe => subscribe());
        },
        error: refreshError => {
          // if silent login fails, redirect user to cloud-sso
          this.redirectToLogin(obs, responseError || refreshError);
        }
      });
  }

  /**
   * Redirect to cloud login and also forward a session expired error in order
   * to complete the request and to suppress any potential error messages.
   * Additionally, page preloader will be shown.
   * In case redirect should be prevented, return a ResponseError.
   * @param obs
   * @param error
   * @private
   */
  private redirectToLogin(obs: Observer<FetchResult>, error: HttpErrorResponse): void {
    this.oauth.getLoginUrl$().subscribe(url => {
      if (this.redirect.redirectTo(url, true, true)) {
        this.preloader.startLoading(PreloadType.MANUAL);
        obs.next({ errors: [new GraphQLError(GQL_SESSION_EXPIRED)] });
      } else {
        obs.error(error);
      }
    });
  }
}
